import { createContext, ReactNode, useContext, useState } from 'react';

/**
 * The navbar mobile state context.
 *
 * @public
 */
export type WbNavbarMobileState = {
  /**
   * Whether or not the current view is on a mobile device.
   */
  isMobile: boolean;

  /**
   * Whether or not the mobile sidebar menu is open.
   */
  isMenuOpen: boolean;

  /**
   * A function to toggle the mobile menu open/closed state.
   */
  toggleMenu: () => void;

  /**
   * A function to explicitly set the menu state.
   */
  setMenuOpen: (open: boolean) => void;
};

const defaultNavbarMobileStateContext: WbNavbarMobileState = {
  isMobile: false,
  isMenuOpen: false,
  toggleMenu: () => {},
  setMenuOpen: () => {},
};

export const WbNavbarMobileStateContext = createContext<WbNavbarMobileState>(
  defaultNavbarMobileStateContext,
);

/**
 * Provides context for reading and updating sidebar mobile state.
 *
 * @public
 */
export function WbNavbarMobileStateProvider(props: {
  children: ReactNode;
  value?: Partial<WbNavbarMobileState>;
}) {
  const { children, value = {} } = props;

  const [isMenuOpen, setIsMenuOpen] = useState(value.isMenuOpen || false);

  const toggleMenu = () => {
    setIsMenuOpen(prevState => !prevState);
  };

  const contextValue: WbNavbarMobileState = {
    isMobile: value.isMobile !== undefined ? value.isMobile : false,
    isMenuOpen,
    toggleMenu: value.toggleMenu || toggleMenu,
    setMenuOpen: value.setMenuOpen || setIsMenuOpen,
  };

  return (
    <WbNavbarMobileStateContext.Provider value={contextValue}>
      {children}
    </WbNavbarMobileStateContext.Provider>
  );
}

/**
 * Hook to read and update the sidebar mobile state.
 *
 * @public
 */
export const useWbSidebarMobileState = (): WbNavbarMobileState => {
  return useContext(WbNavbarMobileStateContext);
};
